// ============================================
// RightSidebarPanel.jsx - Files & Textbooks Panel
// ============================================
// Right sidebar for viewing session files and textbooks
// Features: File list, textbook library, preview, search

import React, { useState, useEffect } from 'react';
import {
  FileText,
  BookOpen,
  Download,
  Eye,
  Trash2,
  Clock,
  RefreshCw,
  Search,
  PanelRightClose,
  Maximize2,
  Minimize2,
  X,
  File,
  ClipboardList,
  PenTool,
  Layers,
  Image,
  Code,
  Upload,
  Loader,
  Check,
  AlertCircle,
  ChevronRight,
  BookMarked,
  GraduationCap,
  MessageSquare,
  Send,
} from 'lucide-react';
import { textbookService, pollTextbookStatus } from '../services/textbookService';
import TextbookSidebarReader from './TextbookSidebarReader';

// ============================================
// DOCUMENT TYPE CONFIG
// ============================================
const DOC_TYPE_CONFIG = {
  lesson_plan: { icon: BookOpen, color: '#10b981', label: 'Lesson Plan' },
  worksheet: { icon: PenTool, color: '#10b981', label: 'Worksheet' },
  quiz: { icon: ClipboardList, color: '#f59e0b', label: 'Quiz' },
  assignment: { icon: File, color: '#8b5cf6', label: 'Assignment' },
  study_guide: { icon: BookOpen, color: '#06b6d4', label: 'Study Guide' },
  rubric: { icon: Layers, color: '#ec4899', label: 'Rubric' },
  notes: { icon: FileText, color: '#6b7280', label: 'Notes' },
  image: { icon: Image, color: '#3b82f6', label: 'Image' },
  code: { icon: Code, color: '#22c55e', label: 'Code' },
  default: { icon: FileText, color: '#10b981', label: 'Document' },
};

const getDocConfig = (type) => DOC_TYPE_CONFIG[type] || DOC_TYPE_CONFIG.default;

// ============================================
// TIME AGO HELPER
// ============================================
function getTimeAgo(date) {
  if (!date) return '';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
}

// ============================================
// DOCUMENT CARD COMPONENT
// ============================================
function DocumentCard({ document, onPreview, onDownload, onDelete, isActive }) {
  const config = getDocConfig(document.type);
  const IconComponent = config.icon;

  return (
    <div
      className={`file-item ${isActive ? 'active' : ''}`}
      onClick={() => onPreview(document)}
    >
      <div
        className="file-icon"
        style={{ background: `${config.color}15`, color: config.color }}
      >
        <IconComponent size={20} />
      </div>

      <div className="file-info">
        <div className="file-title">{document.title || document.name}</div>
        <div className="file-meta">
          <span
            className="file-type-badge"
            style={{ background: `${config.color}20`, color: config.color }}
          >
            {config.label}
          </span>
          <span className="file-time">{getTimeAgo(document.createdAt)}</span>
        </div>
      </div>

      <div className="file-actions">
        <button
          className="file-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDownload(document);
          }}
          title="Download"
        >
          <Download size={14} />
        </button>
        <button
          className="file-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(document.id);
          }}
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// TEXTBOOK CARD COMPONENT
// ============================================
function TextbookCard({ textbook, isActive, onSelect, onDelete }) {
  const statusColors = {
    ready: '#22c55e',
    processing: '#eab308',
    failed: '#ef4444',
    pending: '#6b7280',
  };

  const statusIcons = {
    ready: <Check size={12} />,
    processing: <Loader size={12} className="spinning" />,
    failed: <AlertCircle size={12} />,
    pending: <Clock size={12} />,
  };

  return (
    <div
      className={`file-item ${isActive ? 'active' : ''} ${textbook.status !== 'ready' ? 'disabled' : ''}`}
      onClick={() => textbook.status === 'ready' && onSelect?.(textbook)}
    >
      <div
        className="file-icon"
        style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}
      >
        <BookMarked size={20} />
      </div>

      <div className="file-info">
        <div className="file-title">{textbook.title}</div>
        <div className="file-meta">
          {textbook.pages && (
            <span className="file-pages">{textbook.pages} pages</span>
          )}
          <span
            className="file-status-badge"
            style={{
              background: `${statusColors[textbook.status]}20`,
              color: statusColors[textbook.status],
            }}
          >
            {statusIcons[textbook.status]}
            <span>{textbook.status}</span>
          </span>
        </div>
      </div>

      {textbook.status === 'ready' && (
        <ChevronRight size={16} className="file-arrow" />
      )}

      {textbook.status === 'failed' && (
        <button
          className="file-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(textbook);
          }}
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

// ============================================
// TEXTBOOK UPLOAD ZONE
// ============================================
function TextbookUploadZone({ onUpload, isUploading }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        onUpload(file);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div
      className={`upload-zone-mini ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !isUploading && document.getElementById('textbook-upload-input')?.click()}
    >
      <input
        id="textbook-upload-input"
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      {isUploading ? (
        <>
          <Loader className="spinning" size={18} />
          <span>Uploading...</span>
        </>
      ) : (
        <>
          <Upload size={18} />
          <span>Drop PDF or click to upload</span>
        </>
      )}
    </div>
  );
}

// ============================================
// TEXTBOOK PREVIEW PANEL
// ============================================
function TextbookPreviewPanel({ textbook, onClose, onQuery }) {
  const [activePreviewTab, setActivePreviewTab] = useState('info');
  const [chapters, setChapters] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [isQuerying, setIsQuerying] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (textbook && textbook.status === 'ready') {
      loadTextbookDetails();
    }
  }, [textbook?.id]);

  const loadTextbookDetails = async () => {
    setLoading(true);
    try {
      const [chaptersRes, definitionsRes] = await Promise.all([
        textbookService.getChapters(textbook.id),
        textbookService.getDefinitions(textbook.id),
      ]);

      if (chaptersRes.data) {
        setChapters(chaptersRes.data.chapters || []);
      }
      if (definitionsRes.data) {
        setDefinitions(definitionsRes.data.definitions || []);
      }
    } catch (e) {
      console.error('Failed to load textbook details:', e);
    }
    setLoading(false);
  };

  const handleQuery = async (e) => {
    e.preventDefault();
    if (!query.trim() || isQuerying) return;

    setIsQuerying(true);
    setQueryResult(null);

    try {
      const result = await textbookService.queryTextbook(textbook.id, query, 'student', true);
      if (result.data) {
        setQueryResult(result.data);
      }
    } catch (e) {
      console.error('Query failed:', e);
    }
    setIsQuerying(false);
  };

  if (!textbook) return null;

  return (
    <div className="textbook-preview-panel">
      <div className="preview-header">
        <div className="preview-header-info">
          <BookMarked size={20} style={{ color: '#8b5cf6' }} />
          <span className="preview-title">{textbook.title}</span>
        </div>
        <button className="header-btn" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {/* Preview Tabs */}
      <div className="preview-tabs">
        <button
          className={`preview-tab ${activePreviewTab === 'info' ? 'active' : ''}`}
          onClick={() => setActivePreviewTab('info')}
        >
          <BookOpen size={14} />
          Info
        </button>
        <button
          className={`preview-tab ${activePreviewTab === 'chapters' ? 'active' : ''}`}
          onClick={() => setActivePreviewTab('chapters')}
        >
          <Layers size={14} />
          Chapters
        </button>
        <button
          className={`preview-tab ${activePreviewTab === 'definitions' ? 'active' : ''}`}
          onClick={() => setActivePreviewTab('definitions')}
        >
          <GraduationCap size={14} />
          Definitions
        </button>
        <button
          className={`preview-tab ${activePreviewTab === 'ask' ? 'active' : ''}`}
          onClick={() => setActivePreviewTab('ask')}
        >
          <MessageSquare size={14} />
          Ask
        </button>
      </div>

      <div className="preview-content">
        {loading ? (
          <div className="preview-loading">
            <Loader className="spinning" size={24} />
            <span>Loading textbook...</span>
          </div>
        ) : activePreviewTab === 'info' ? (
          <div className="textbook-info">
            <div className="info-card">
              <div className="info-row">
                <span className="info-label">Title</span>
                <span className="info-value">{textbook.title}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Pages</span>
                <span className="info-value">{textbook.pages || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Chunks</span>
                <span className="info-value">{textbook.chunks || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Subject</span>
                <span className="info-value">{textbook.subject || 'Not specified'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Queries</span>
                <span className="info-value">{textbook.queries || 0}</span>
              </div>
            </div>
          </div>
        ) : activePreviewTab === 'chapters' ? (
          <div className="chapters-list">
            {chapters.length > 0 ? (
              chapters.map((chapter, idx) => (
                <div key={idx} className="chapter-item">
                  <div className="chapter-name">{chapter.name}</div>
                  <div className="chapter-meta">
                    {chapter.page_start && (
                      <span>Pages {chapter.page_start}-{chapter.page_end}</span>
                    )}
                    <span>{chapter.chunk_count} chunks</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-preview">
                <Layers size={24} />
                <span>No chapters detected</span>
              </div>
            )}
          </div>
        ) : activePreviewTab === 'definitions' ? (
          <div className="definitions-list">
            {definitions.length > 0 ? (
              definitions.slice(0, 50).map((def, idx) => (
                <div key={idx} className="definition-item">
                  <div className="definition-term">{def.term}</div>
                  <div className="definition-content">{def.definition}</div>
                  {def.page && (
                    <div className="definition-page">p. {def.page}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-preview">
                <GraduationCap size={24} />
                <span>No definitions extracted</span>
              </div>
            )}
          </div>
        ) : (
          <div className="ask-section">
            <form onSubmit={handleQuery} className="ask-form">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask a question about this textbook..."
                disabled={isQuerying}
              />
              <button type="submit" disabled={isQuerying || !query.trim()}>
                {isQuerying ? <Loader className="spinning" size={16} /> : <Send size={16} />}
              </button>
            </form>

            {queryResult && (
              <div className="query-result">
                <div className="query-question">
                  <strong>Q:</strong> {queryResult.question}
                </div>
                {queryResult.response ? (
                  <div className="query-answer">
                    <strong>A:</strong> {queryResult.response}
                  </div>
                ) : (
                  <div className="query-context">
                    <strong>Relevant passages:</strong>
                    {queryResult.results?.map((r, idx) => (
                      <div key={idx} className="context-item">
                        <div className="context-content">{r.content}</div>
                        <div className="context-meta">
                          {r.chapter} • Pages {r.pages} • Score: {r.score}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// TEXTBOOKS TAB CONTENT
// ============================================
function TextbooksTabContent({ onSelectTextbook }) {
  const [textbooks, setTextbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTextbook, setSelectedTextbook] = useState(null);
  const [processingBooks, setProcessingBooks] = useState([]);

  useEffect(() => {
    loadTextbooks();
  }, []);

  const loadTextbooks = async () => {
    setLoading(true);
    const result = await textbookService.listTextbooks();
    
    if (result.error) {
      setError(result.error);
    } else {
      const books = result.data?.textbooks || [];
      setTextbooks(books);
      // Track processing books
      const processing = books.filter(t => t.status === 'processing');
      setProcessingBooks(processing);
      
      // Start polling for processing books
      processing.forEach(book => {
        pollTextbookStatus(
          book.id,
          null,
          (completedBook) => {
            setTextbooks(prev => prev.map(t => t.id === completedBook.id ? completedBook : t));
            setProcessingBooks(prev => prev.filter(t => t.id !== completedBook.id));
          },
          null
        );
      });
    }
    setLoading(false);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setError(null);

    const result = await textbookService.uploadTextbook(file, file.name);

    setUploading(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    const newTextbook = {
      id: result.data.textbook_id,
      title: result.data.title,
      status: result.data.status,
    };

    setTextbooks(prev => [newTextbook, ...prev]);

    if (result.status === 202) {
      setProcessingBooks(prev => [newTextbook, ...prev]);
      
      // Poll for completion
      pollTextbookStatus(
        newTextbook.id,
        (data) => {
          setTextbooks(prev => prev.map(t => t.id === data.id ? data : t));
        },
        (completedBook) => {
          setTextbooks(prev => prev.map(t => t.id === completedBook.id ? completedBook : t));
          setProcessingBooks(prev => prev.filter(t => t.id !== completedBook.id));
        },
        (err) => {
          setTextbooks(prev => prev.map(t => 
            t.id === newTextbook.id ? { ...t, status: 'failed', error: err.error } : t
          ));
          setProcessingBooks(prev => prev.filter(t => t.id !== newTextbook.id));
        }
      );
    }
  };

  const handleSelect = (textbook) => {
    setSelectedTextbook(textbook);
    onSelectTextbook?.(textbook);
  };

  const handleDelete = async (textbook) => {
    await textbookService.deleteTextbook(textbook.id);
    setTextbooks(prev => prev.filter(t => t.id !== textbook.id));
    if (selectedTextbook?.id === textbook.id) {
      setSelectedTextbook(null);
    }
  };

  if (selectedTextbook) {
    return (
      <TextbookSidebarReader
        textbook={selectedTextbook}
        onClose={() => setSelectedTextbook(null)}
      />
    );
  }

  return (
    <div className="textbooks-content">
      {/* Upload Zone */}
      <TextbookUploadZone onUpload={handleUpload} isUploading={uploading} />

      {/* Error */}
      {error && (
        <div className="upload-error-mini">
          <AlertCircle size={14} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={12} /></button>
        </div>
      )}

      {/* Textbook List */}
      <div className="textbooks-list">
        {loading ? (
          <div className="loading-state-mini">
            <Loader className="spinning" size={20} />
            <span>Loading textbooks...</span>
          </div>
        ) : textbooks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <BookMarked size={28} />
            </div>
            <h4>No textbooks yet</h4>
            <p>Upload a PDF to use as reference</p>
          </div>
        ) : (
          textbooks.map(textbook => (
            <TextbookCard
              key={textbook.id}
              textbook={textbook}
              isActive={selectedTextbook?.id === textbook.id}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ============================================
// PREVIEW PANEL COMPONENT (for documents)
// ============================================
function PreviewPanel({ document, onClose }) {
  if (!document) return null;

  const config = getDocConfig(document.type);

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <span className="preview-title">{document.title || document.name}</span>
        <button className="header-btn" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      <div className="preview-content">
        {document.content ? (
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6 }}>
            {document.content}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Eye size={24} />
            </div>
            <h4>Preview not available</h4>
            <p>Download the file to view its contents</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN RIGHT SIDEBAR COMPONENT
// ============================================
export default function RightSidebarPanel({
  documents = [],
  onClose,
  onDocumentSelect,
  onDocumentDownload,
  onDocumentDelete,
  onRefresh,
  isExpanded,
  onToggleExpand,
  onSelectTextbook,
}) {
  const [activeTab, setActiveTab] = useState('documents');
  const [search, setSearch] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Filter documents
  const filteredDocuments = documents.filter(doc =>
    (doc.title || doc.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handlePreview = (doc) => {
    setSelectedDocument(doc);
    setShowPreview(true);
    onDocumentSelect?.(doc);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedDocument(null);
  };

  return (
    <div className="sidebar-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="right-sidebar-header">
        <h3>Resources</h3>
        <div className="right-header-actions">
          <button className="header-btn" onClick={onRefresh} title="Refresh">
            <RefreshCw size={16} />
          </button>
          <button className="header-btn" onClick={onToggleExpand} title={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button className="header-btn" onClick={onClose} title="Close">
            <PanelRightClose size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="sidebar-tabs">
        <button
          className={`sidebar-tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FileText size={14} />
          Documents
        </button>
        <button
          className={`sidebar-tab ${activeTab === 'textbooks' ? 'active' : ''}`}
          onClick={() => setActiveTab('textbooks')}
        >
          <BookMarked size={14} />
          Textbooks
        </button>
      </div>

      {/* Search (only for documents) */}
      {activeTab === 'documents' && documents.length > 0 && (
        <div className="sidebar-search" style={{ margin: '12px' }}>
          <Search size={14} />
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')}>
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="documents-list" style={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'documents' ? (
          filteredDocuments.length > 0 ? (
            filteredDocuments.map(doc => (
              <DocumentCard
                key={doc.id}
                document={doc}
                isActive={selectedDocument?.id === doc.id}
                onPreview={handlePreview}
                onDownload={onDocumentDownload}
                onDelete={onDocumentDelete}
              />
            ))
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FileText size={28} />
              </div>
              <h4>No documents yet</h4>
              <p>Files generated in this chat will appear here</p>
            </div>
          )
        ) : (
          <TextbooksTabContent onSelectTextbook={onSelectTextbook} />
        )}
      </div>

      {/* Preview Panel (when document selected) */}
      {showPreview && selectedDocument && activeTab === 'documents' && (
        <PreviewPanel
          document={selectedDocument}
          onClose={handleClosePreview}
        />
      )}

      <style>{`
        /* Upload Zone Mini */
        .upload-zone-mini {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px;
          margin: 12px;
          border: 2px dashed var(--gi-gray-700, #374151);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          color: var(--gi-gray-500, #6b7280);
          font-size: 12px;
        }

        .upload-zone-mini:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
          background: rgba(139, 92, 246, 0.05);
        }

        .upload-zone-mini.dragging {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.1);
        }

        .upload-zone-mini.uploading {
          pointer-events: none;
          opacity: 0.7;
        }

        /* Error Mini */
        .upload-error-mini {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          margin: 0 12px 8px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 6px;
          color: #ef4444;
          font-size: 11px;
        }

        .upload-error-mini button {
          margin-left: auto;
          background: transparent;
          border: none;
          color: #ef4444;
          cursor: pointer;
          padding: 2px;
        }

        /* Loading State Mini */
        .loading-state-mini {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 32px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 12px;
        }

        /* Textbook specific styles */
        .file-item.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .file-pages {
          color: var(--gi-gray-500, #6b7280);
          font-size: 11px;
        }

        .file-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          text-transform: capitalize;
        }

        .file-arrow {
          color: var(--gi-gray-500, #6b7280);
        }

        /* Textbook Preview Panel */
        .textbook-preview-panel {
          position: absolute;
          inset: 0;
          background: var(--gi-gray-900, #111827);
          display: flex;
          flex-direction: column;
          z-index: 10;
        }

        .preview-header-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .preview-tabs {
          display: flex;
          gap: 4px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--gi-gray-800, #1f2937);
        }

        .preview-tab {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 10px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .preview-tab:hover {
          background: var(--gi-gray-800, #1f2937);
          color: var(--gi-gray-300, #d1d5db);
        }

        .preview-tab.active {
          background: rgba(139, 92, 246, 0.15);
          color: #a78bfa;
        }

        .preview-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 48px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
        }

        .empty-preview {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 48px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
        }

        /* Info Card */
        .textbook-info {
          padding: 12px;
        }

        .info-card {
          background: var(--gi-gray-800, #1f2937);
          border-radius: 8px;
          padding: 12px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid var(--gi-gray-700, #374151);
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-label {
          color: var(--gi-gray-500, #6b7280);
          font-size: 12px;
        }

        .info-value {
          color: var(--gi-gray-200, #e5e7eb);
          font-size: 12px;
          font-weight: 500;
        }

        /* Chapters List */
        .chapters-list {
          padding: 12px;
        }

        .chapter-item {
          padding: 10px 12px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 6px;
          margin-bottom: 8px;
        }

        .chapter-name {
          color: var(--gi-gray-200, #e5e7eb);
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .chapter-meta {
          display: flex;
          gap: 12px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 11px;
        }

        /* Definitions List */
        .definitions-list {
          padding: 12px;
        }

        .definition-item {
          padding: 10px 12px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 6px;
          margin-bottom: 8px;
        }

        .definition-term {
          color: #a78bfa;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .definition-content {
          color: var(--gi-gray-300, #d1d5db);
          font-size: 12px;
          line-height: 1.5;
        }

        .definition-page {
          color: var(--gi-gray-500, #6b7280);
          font-size: 10px;
          margin-top: 4px;
        }

        /* Ask Section */
        .ask-section {
          padding: 12px;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .ask-form {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .ask-form input {
          flex: 1;
          padding: 10px 12px;
          background: var(--gi-gray-800, #1f2937);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 13px;
        }

        .ask-form input:focus {
          outline: none;
          border-color: #8b5cf6;
        }

        .ask-form button {
          padding: 10px 14px;
          background: #8b5cf6;
          border: none;
          border-radius: 8px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .ask-form button:hover:not(:disabled) {
          background: #7c3aed;
        }

        .ask-form button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .query-result {
          flex: 1;
          overflow-y: auto;
        }

        .query-question {
          padding: 10px 12px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 8px;
          margin-bottom: 8px;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 13px;
        }

        .query-answer {
          padding: 12px;
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          color: var(--gi-gray-200, #e5e7eb);
          font-size: 13px;
          line-height: 1.6;
        }

        .query-context {
          padding: 12px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 8px;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 13px;
        }

        .context-item {
          padding: 10px;
          background: var(--gi-gray-900, #111827);
          border-radius: 6px;
          margin-top: 8px;
        }

        .context-content {
          color: var(--gi-gray-300, #d1d5db);
          font-size: 12px;
          line-height: 1.5;
          margin-bottom: 6px;
        }

        .context-meta {
          color: var(--gi-gray-500, #6b7280);
          font-size: 10px;
        }

        /* Spinning animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
